import { handleResponseOld, handleResponseWithNon200Errors, makePost } from './helpers';

/**
 * @category Mainhost API
 * @see postPlaceAbsenteeBid
 */
type PostPlaceAbsenteeBidParams = {
    authToken: string;
    deployment: string;
    details: any;
};

export type PostPlaceAbsenteeBidResponse = {
    currentBid: number;
    isCurrentHighBid: boolean;
    isReserveMet: boolean;
    leadingBid: number;
    message: string;
    success: boolean;
};

/**
 * makes POST to mainhost API absentee bidder service
 * @function postPlaceAbsenteeBid
 * @category Mainhost API
 * @param {PostPlaceAbsenteeBidParams}
 * @see https://mainhost-DEPLOYMENT.liveauctioneers.com/api/abs_bid
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/libs/MOBILE_API.php#L555
 */
export const postPlaceAbsenteeBid = ({ authToken, deployment, details }: PostPlaceAbsenteeBidParams) => {
    return new Promise<PostPlaceAbsenteeBidResponse>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<MAINHOST-API>api/abs_bid' });
        request.type('form');
        request.send({
            accept_terms: true,
            bid: details.bidAmount,
            cat_id: details.catalogId,
            client: details.client,
            device_type: details.deviceType,
            lot_id: details.itemId,
        });
        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response,
                transform: (body) => {
                    const payload: PostPlaceAbsenteeBidResponse = {
                        currentBid: Number(body.current_bid),
                        isCurrentHighBid: body.is_current_high_bid,
                        isReserveMet: body.is_reserve_met,
                        leadingBid: Number(body.leading_bid),
                        message: body.message,
                        success: body.success,
                    };

                    return payload;
                },
            })
        );
    });
};

/**
 * @category Auction-Engine API
 * @see postPlaceLiveBidResponse
 */
type PostPlaceLiveBidResponse = {
    error: boolean;
    payload: string;
};

/**
 * @category Auction-Engine API
 * @see postPlaceLiveBid
 */
type PostPlaceLiveBidParams = {
    authToken: string;
    bidAmount: number;
    catalogId: number;
    deployment: string;
    itemId: number;
    sellerId: number;
    source: string;
};

/**
 * POST a bid to clerking API for live auction bidding
 * @function postPlaceLiveBid
 * @category Auction-Engine API
 * @param {PostPlaceLiveBidParams}
 * @see https://auction-service-DEPLOYMENT.liveauctioneers.com/clerk/houses/${sellerId}/auctions/${catalogId}/lots/${itemId}/bids/place
 */
export const postPlaceLiveBid = ({
    authToken,
    bidAmount,
    catalogId,
    deployment,
    itemId,
    sellerId,
    source,
}: PostPlaceLiveBidParams) => {
    return new Promise<PostPlaceLiveBidResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: `clerk/houses/${sellerId}/auctions/${catalogId}/lots/${itemId}/bids/place`,
            authToken,
            deployment,
            path: '<AUCTION-ENGINE-API>',
        });
        request.send({
            amount: bidAmount,
            bidSource: source,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });
};

/**
 * @category Mainhost API
 * @see postRetractBid
 */
type PostRetractBidParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
    itemId: number;
    retractionExplanation: any;
    retractionReason: any;
};

/**
 * makes POST request to mainhost retact bid API
 * @function postRetractBid
 * @category Mainhost API
 * @param {PostRetractBidParams}
 * @see https://mainhost-DEPLOYMENT.liveauctioneers.com/api/bidders/${bidderId}/biditems/${itemId}/retract
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php#L1540
 */
export const postRetractBid = ({
    authToken,
    bidderId,
    deployment,
    itemId,
    retractionExplanation,
    retractionReason,
}: PostRetractBidParams) => {
    return new Promise<void>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MAINHOST-API>api/bidders/${bidderId}/biditems/${itemId}/retract`,
        });
        request.send({
            retractionExplanation,
            retractionReason,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
};
